import {
  Avatar,
  Box,
  Card,
  CardActionArea,
  Stack,
  Typography,
} from '@mui/material'
import { FC } from 'react'
import { Icons } from 'utoniq-ui'

import {
  ArtistFavoriteMember,
  Avatar as AvatarType,
  Maybe,
} from '@/clients/utoniq-core/schema'
import { styleSets } from '@/components/styles'

type FavoriteMemberSelectProps = {
  favoriteMembers: Array<
    Maybe<
      Pick<ArtistFavoriteMember, 'name' | 'id'> & {
        avatar: Maybe<Pick<AvatarType, 'uri'>>
      }
    >
  >
  selectedFavoriteMember?: string
  onSelectFavoriteMember: (memberId: string) => void
}

/**
 * FavoriteMemberSelect
 * 推しメンバーを選択するコンポーネント
 */

export const MembershipFavoriteMemberSelect: FC<FavoriteMemberSelectProps> = ({
  favoriteMembers,
  selectedFavoriteMember,
  onSelectFavoriteMember,
}) => {
  return (
    <Stack
      direction="row"
      flexWrap="wrap"
      sx={{
        justifyContent: 'space-between',
        '&::after': {
          content: '""',
          display: 'block',
          height: '100%',
          width: 'calc(33.33% - 8px)',
          mb: 2,
        },
      }}
    >
      {favoriteMembers.map(member => (
        <Box
          key={member?.id}
          sx={{ height: '100%', width: 'calc(33.33% - 8px)', mb: 2 }}
        >
          <Card
            elevation={0}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <CardActionArea
              onClick={() => onSelectFavoriteMember(member?.id ?? '')}
              sx={{
                bgcolor:
                  selectedFavoriteMember === member?.id
                    ? 'primary.light'
                    : 'background.default',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Box sx={{ position: 'relative', my: 2 }}>
                {/* 外側の選択円 */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: -4,
                    left: -4,
                    right: -4,
                    bottom: -4,
                    borderRadius: '50%',
                    border: '4px solid',
                    borderColor:
                      selectedFavoriteMember === member?.id
                        ? 'primary.main'
                        : 'grey.100',
                    pointerEvents: 'none',
                  }}
                />
                {/* 内側の白い境界線 */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: -1,
                    left: -1,
                    right: -1,
                    bottom: -1,
                    borderRadius: '50%',
                    border: '1px solid',
                    borderColor: 'background.default',
                    pointerEvents: 'none',
                  }}
                />
                {/* アバター */}
                <Avatar
                  src={member?.avatar?.uri ?? ''}
                  sx={{
                    width: '56px',
                    height: '56px',
                  }}
                />
                {/* チェックマーク */}
                {selectedFavoriteMember === member?.id && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: -4,
                      right: -4,
                      width: '24px',
                      height: '24px',
                      borderRadius: '50%',
                      bgcolor: 'primary.main',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                    }}
                  >
                    <Icons.Check fontSize="small" sx={{ color: 'white' }} />
                  </Box>
                )}
              </Box>
              {/* 推しメンバー名 */}
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{
                  textAlign: 'center',
                  mb: 1,
                  width: '100%',
                  ...styleSets.lineCamp(1),
                }}
              >
                {member?.name}
              </Typography>
            </CardActionArea>
          </Card>
        </Box>
      ))}
    </Stack>
  )
}
